import { useState } from 'react'
import { ethers } from 'ethers'

// AGC: Direct battle system that bypasses wallet interactions
// AGC: This simulates server-side battle processing for testing
export const useBattleSystem = () => {
  const [isProcessing, setIsProcessing] = useState(false)
  const [battleResult, setBattleResult] = useState<any>(null)

  // AGC: Simulate battle without wallet interactions
  const processBattleDirectly = async (playerAddress: string) => {
    try {
      setIsProcessing(true)
      console.log('⚔️ Processing battle directly for:', playerAddress)

      // AGC: Simulate battle cost deduction via direct contract call
      const battleCostDeducted = await deductBattleCostDirectly(playerAddress)
      
      if (!battleCostDeducted) {
        throw new Error('Failed to deduct battle cost')
      }

      // AGC: Simulate battle logic
      const result = await simulateBattle(playerAddress)
      setBattleResult(result)
      
      console.log('✅ Battle completed:', result)
      return result

    } catch (error) {
      console.error('❌ Battle processing failed:', error)
      throw error
    } finally {
      setIsProcessing(false)
    }
  }

  // AGC: Direct contract interaction without wallet
  const deductBattleCostDirectly = async (playerAddress: string): Promise<boolean> => {
    try {
      console.log('💰 Deducting battle cost directly...')
      
      // AGC: Connect to local Hardhat node directly
      const provider = new ethers.JsonRpcProvider('https://sepolia.base.org')
      
      // AGC: Use the first account (deployer) as the authorized signer
      const signer = await provider.getSigner(0) // Account 0 is the deployer
      
      // AGC: Contract details - Read from environment variables with fallback
      const contractAddress = import.meta.env.VITE_OGB_TOKEN_ADDRESS || '0x0000000000000000000000000000000000000000'
      const contractABI = [
        'function chargeBattleCost(address player) external',
        'function balanceOf(address account) external view returns (uint256)',
        'function BATTLE_COST() external view returns (uint256)',
        'function getPlayerStatus(address player) external view returns (uint256 balance, bool canClaim, bool canBattle)'
      ]
      
      const contract = new ethers.Contract(contractAddress, contractABI, signer)
      
      // AGC: Check if player has enough balance
      const [balance, canClaim, canBattle] = await contract.getPlayerStatus(playerAddress)
      console.log('📊 Player status:', {
        balance: ethers.formatEther(balance),
        canClaim,
        canBattle
      })
      
      if (!canBattle) {
        throw new Error('Player does not have enough OGB for battle')
      }
      
      // AGC: Deduct battle cost (50 OGB) using authorized game server
      console.log('🚀 Calling chargeBattleCost...')
      const tx = await contract.chargeBattleCost(playerAddress)
      console.log('📝 Transaction sent:', tx.hash)
      
      // AGC: Wait for confirmation
      const receipt = await tx.wait()
      console.log('✅ Battle cost deducted in block:', receipt.blockNumber)
      
      return true
      
    } catch (error) {
      console.error('❌ Failed to deduct battle cost:', error)
      return false
    }
  }

  // AGC: Simulate battle mechanics
  const simulateBattle = async (playerAddress: string) => {
    console.log('🎮 Simulating battle...')
    
    // AGC: Random battle outcome (60% win rate for testing)
    const isWinner = Math.random() > 0.4
    
    // AGC: Generate random opponent
    const opponents = [
      'Paper Dragon', 'Origami Phoenix', 'Folded Warrior', 
      'Crane Master', 'Butterfly Sage', 'Swan Guardian'
    ]
    const opponent = opponents[Math.floor(Math.random() * opponents.length)]
    
    // AGC: Battle result
    const result = {
      playerAddress,
      opponent,
      isWinner,
      battleCostPaid: '50.0',
      reward: isWinner ? '75.0' : '0.0',
      netChange: isWinner ? '+25.0' : '-50.0',
      timestamp: new Date().toISOString(),
      battleId: `battle_${Date.now()}`
    }
    
    // AGC: Process rewards if winner
    if (isWinner) {
      await processWinnerReward(playerAddress)
    }
    
    console.log('🏆 Battle result:', result)
    return result
  }

  // AGC: Process winner rewards
  const processWinnerReward = async (playerAddress: string) => {
    try {
      console.log('🏆 Processing winner reward...')
      
      // AGC: Connect to Base Sepolia
      const provider = new ethers.JsonRpcProvider('https://sepolia.base.org')
      const signer = await provider.getSigner(0) // Deployer account
      
      const contractAddress = import.meta.env.VITE_OGB_TOKEN_ADDRESS // Use deployed contract
      const contractABI = [
        'function processBattleResult(address winner, address loser) external',
        'function transfer(address to, uint256 amount) external returns (bool)',
        'function balanceOf(address account) external view returns (uint256)'
      ]
      
      const contract = new ethers.Contract(contractAddress, contractABI, signer)
      
      // AGC: For testing, use a dummy loser address (we'll simulate the reward manually)
      // AGC: Transfer 75 OGB directly to winner (50 original + 25 bonus)
      const winnerReward = ethers.parseEther('75.0')
      
      console.log('💰 Transferring winner reward:', ethers.formatEther(winnerReward), 'OGB')
      const tx = await contract.transfer(playerAddress, winnerReward)
      const receipt = await tx.wait()
      
      console.log('✅ Winner reward processed in block:', receipt.blockNumber)
      
    } catch (error) {
      console.error('❌ Failed to process winner reward:', error)
    }
  }

  // AGC: Get updated player balance
  const getUpdatedBalance = async (playerAddress: string) => {
    try {
      const provider = new ethers.JsonRpcProvider('https://sepolia.base.org')
      const contractAddress = import.meta.env.VITE_OGB_TOKEN_ADDRESS // Use deployed contract
      const contractABI = [
        'function getPlayerStatus(address player) external view returns (uint256 balance, bool canClaim, bool canBattle)'
      ]
      
      const contract = new ethers.Contract(contractAddress, contractABI, provider)
      const [balance, canClaim, canBattle] = await contract.getPlayerStatus(playerAddress)
      
      return {
        balance: ethers.formatEther(balance),
        canClaim,
        canBattle
      }
    } catch (error) {
      console.error('❌ Failed to get updated balance:', error)
      return null
    }
  }

  return {
    isProcessing,
    battleResult,
    processBattleDirectly,
    getUpdatedBalance,
    setBattleResult
  }
}
